/*
 * Copyright (c) CIRROM 2023.
 */

import {
  CountryBreakdownItem,
  getCountryBreakdown,
  IntegratedSystemDetail,
} from "../../api/integratedsystems";
import React, { useCallback, useState } from "react";
import { a, useSpring } from "@react-spring/web";
import classes from "./NewCRMPanel.module.css";
import Typography from "@mui/material/Typography";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import { ComplianceMap } from "../compliance/ComplianceMap";
import { AgGridReact } from "ag-grid-react";
import { countPercentageCellRenderer } from "../suggested-changes/CellRenderers";

export const ContactsMap = (
  props: Readonly<{ systemDetails: IntegratedSystemDetail }>
) => {
  /**
   * Panel of the contacts in a system/data project on a map
   * */
  const { systemDetails } = props;
  const [selectedMarkerTitle, setSelectedMarkerTitle] = useState<string>(null);
  const [selectedMarkerBreakdown, setSelectedMarkerBreakdown] =
    useState<CountryBreakdownItem[]>(undefined);

  const [displayLocationAsMap, setDisplayLocationAsMap] =
    useState<boolean>(true);

  const [flipToLocationDetails, setFlipToLocationDetails] =
    useState<boolean>(false);

  const { transform, opacity } = useSpring({
    opacity: flipToLocationDetails ? 1 : 0,
    transform: `perspective(600px) rotateX(${
      flipToLocationDetails ? 180 : 0
    }deg)`,

    config: { mass: 5, tension: 250, friction: 50 },
  });

  const getMarkerBreakdown = useCallback(
    (_event, index) => {
      if (index) {
        setSelectedMarkerTitle(
          systemDetails.crm_stats.contact_locations[index].title
        );
        getCountryBreakdown(systemDetails.crm_stats.contact_locations[index].id)
          .then((breakdownResponse) => {
            setSelectedMarkerBreakdown(breakdownResponse);
          })
          .catch((err) => console.log(err));
      }
    },
    [systemDetails]
  );

  return (
    <>
      <a.div
        className={classes.contactLocationMap}
        style={{
          opacity: opacity.to((o) => 1 - o),
          transform,
          display: opacity.to((o) => `${o >= 0.65 ? "none" : "inherit"}`),
        }}
      >
        <div className={classes.map}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
            }}
          >
            <Typography variant={"h3"} style={{ fontWeight: "bold" }}>
              Contacts Location
            </Typography>
            <ToggleButtonGroup
              exclusive
              color={"primary"}
              value={displayLocationAsMap}
              onChange={(_event, value) => setDisplayLocationAsMap(value)}
              style={{ marginRight: "10px" }}
            >
              <ToggleButton
                value={true}
                size={"small"}
                style={{
                  padding: "0px 5px",
                  textTransform: "capitalize",
                }}
              >
                Map
              </ToggleButton>
              <ToggleButton
                value={false}
                size={"small"}
                style={{
                  padding: "0px 5px",
                  textTransform: "capitalize",
                }}
              >
                List
              </ToggleButton>
            </ToggleButtonGroup>
          </div>

          {displayLocationAsMap && systemDetails && (
            <ComplianceMap
              systemDetails={systemDetails}
              onMarkerClick={(event, index) => {
                setFlipToLocationDetails(!flipToLocationDetails);
                getMarkerBreakdown(event, index);
              }}
            />
          )}
          {!displayLocationAsMap && (
            <div style={{ height: "90%" }}>
              <AgGridReact
                getRowHeight={(params) => (params.node.group ? 45 : 35)}
                headerHeight={40}
                columnDefs={[
                  { field: "title", headerName: "Country" },
                  {
                    field: "count",
                    headerName: "Total Contacts",
                    sortable: true,
                  },
                  {
                    field: "count",
                    headerName: "Total Contacts(%)",
                    sortable: true,
                    cellRenderer: "countPercentageCellRenderer",
                  },
                  { field: "id", headerName: "id", hide: true },
                ]}
                rowData={systemDetails?.crm_stats.contact_locations || []}
                components={{
                  countPercentageCellRenderer: countPercentageCellRenderer,
                }}
                onRowDataUpdated={(params) => {
                  params.api.sizeColumnsToFit();
                }}
              />
            </div>
          )}
        </div>
      </a.div>
      <a.div
        className={classes.contactLocationMap}
        style={{
          opacity: opacity,
          transform,
          rotateX: "180deg",
          display: opacity.to((o) => `${o < 0.65 ? "none" : "inherit"}`),
        }}
        onClick={() => setFlipToLocationDetails(!flipToLocationDetails)}
      >
        <div>
          <Typography
            variant={"h3"}
            style={{ fontWeight: "bold" }}
            gutterBottom
          >
            {`Contacts in ${selectedMarkerTitle}`}
          </Typography>
          <div style={{ height: "250px" }}>
            <AgGridReact
              getRowHeight={(params) => (params.node.group ? 45 : 35)}
              headerHeight={40}
              columnDefs={[
                { field: "first_name", headerName: "First Name" },
                { field: "last_name", headerName: "Last Name" },
                { field: "company", headerName: "Company" },
                { field: "city", headerName: "City" },
              ]}
              rowData={selectedMarkerBreakdown}
              onGridReady={(params) => params.api.sizeColumnsToFit()}
              onRowDataUpdated={(params) => {
                params.api.sizeColumnsToFit();
              }}
            />
          </div>
        </div>
      </a.div>
    </>
  );
};
